<script lang="ts" setup>
  import DropdownTemplates from '@/components/UIKit/Dropdown/DropdownTemplates.vue';
  import BcCheckbox from '@/ui-kit/components/BcCheckbox/BcCheckbox.vue';
  import BcButton from '@/ui-kit/components/BcButton/BcButton.vue';
  import BcSpinner from '@/ui-kit/components/BcSpinner/BcSpinner.vue';
  import { NotesTemplate } from '@/domains/models/NotesTemplate';
  import { ref } from 'vue';
  import { User } from '@/domains/models/User';

  defineProps<{
    canPin: boolean;
    userProfile: User;
    templates: NotesTemplate[];
    pin: boolean;
    isValid: boolean;
    images: any[];
    tooltipContent: string;
    containerSize: any;
    isLoading?: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'select-template', value: NotesTemplate): void;
    (e: 'add-image', value: unknown): void;
    (e: 'send-note'): void;
    (e: 'update:pin', value: boolean): void;
  }>();

  const handleSelectTemplate = (template: NotesTemplate) => {
    emit('select-template', template);
  };

  // images
  const fileInput = ref<HTMLInputElement | null>(null);
  const addImage = () => {
    fileInput.value?.click();
  };
  const toBase64 = (file: File) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });
  };
  const handleFileSelect = async(e: Event) => {
    const target = e.target as HTMLInputElement;
    const file = target?.files?.[0];
    if (file) {
      const base64 = await toBase64(file);
      emit('add-image', base64);
    }
  };

  const sendNote = () => {
    emit('send-note');
  };
</script>

<template>
  <div class="flex items-center gap-2.5">
    <DropdownTemplates
      :is-minified="canPin && containerSize.width.value < 555"
      :templates="templates"
      :user-id="userProfile._id"
      class="shrink-0"
      @template-selected="handleSelectTemplate">
    </DropdownTemplates>
    <div
      v-tooltip="tooltipContent"
      class="timeline-editor__button"
    >
      <bc-button
        v-tooltip="{ content: $t('generics.add-image') }"
        class="!h-8 !w-8 !p-2"
        type="outlined"
        @click="addImage"
      >
          <span>
            <svg
              fill="none" height="12" viewBox="0 0 12 12" width="12"
              xmlns="http://www.w3.org/2000/svg">
              <path
                clip-rule="evenodd"
                d="M9.52941 0C9.83066 0 10.0749 0.244208 10.0749 0.545454V1.92513H11.4545C11.7558 1.92513 12 2.16934 12 2.47059C12 2.77183 11.7558 3.01604 11.4545 3.01604H10.0749V4.39572C10.0749 4.69697 9.83066 4.94117 9.52941 4.94117C9.22817 4.94117 8.98396 4.69697 8.98396 4.39572V3.01604H7.60428C7.30303 3.01604 7.05882 2.77183 7.05882 2.47059C7.05882 2.16934 7.30303 1.92513 7.60428 1.92513H8.98396V0.545454C8.98396 0.244208 9.22817 0 9.52941 0ZM2.57725 0.641711L5.03743 0.641711C5.33868 0.641711 5.58289 0.885919 5.58289 1.18717C5.58289 1.48841 5.33868 1.73262 5.03743 1.73262H2.59893C2.23054 1.73262 1.98705 1.73304 1.8004 1.74829C1.62012 1.76302 1.54141 1.78882 1.49385 1.81305C1.35499 1.88381 1.2421 1.9967 1.17135 2.13556C1.14711 2.18312 1.12132 2.26183 1.10659 2.44211C1.09134 2.62876 1.09091 2.87225 1.09091 3.24064V9.04294L2.75441 7.36752L2.76974 7.35234C3.00995 7.11412 3.21323 6.91253 3.39342 6.76076C3.58234 6.60164 3.78123 6.46598 4.02219 6.38813C4.38772 6.27004 4.78115 6.27004 5.14668 6.38813C5.38764 6.46598 5.58653 6.60164 5.77545 6.76076C5.95564 6.91253 6.15893 7.11413 6.39914 7.35235L7.13253 8.07963C7.31428 7.90035 7.4742 7.74635 7.61931 7.62432C7.81165 7.46257 8.01432 7.32483 8.25981 7.24739C8.63162 7.13009 9.03119 7.13469 9.40021 7.2605C9.64385 7.34357 9.8433 7.48593 10.0319 7.65206C10.1062 7.71754 10.1844 7.79183 10.2674 7.87401V6.96256C10.2674 6.66132 10.5116 6.41711 10.8128 6.41711C11.1141 6.41711 11.3583 6.66132 11.3583 6.96256V9.42273C11.3583 9.76366 11.3583 10.052 11.339 10.2884C11.3188 10.5362 11.2746 10.7744 11.159 11.0014C10.9836 11.3455 10.7038 11.6253 10.3597 11.8007C10.1573 11.9038 9.94661 11.9498 9.72829 11.9731C9.4748 12 9.16196 12 8.78848 12L2.57727 12C2.23634 12 1.94795 12 1.71157 11.9807C1.46382 11.9605 1.22557 11.9163 0.998587 11.8007C0.654462 11.6253 0.374679 11.3455 0.199339 11.0014C0.101436 10.8093 0.0550131 10.6098 0.0306625 10.4032C-4.25123e-05 10.1428 -2.25723e-05 9.82017 1.32372e-06 9.43368L2.29907e-06 3.21896C-6.31651e-06 2.87804 -1.3577e-05 2.58966 0.0192997 2.35327C0.0395413 2.10553 0.0836866 1.86727 0.199338 1.6403C0.374679 1.29617 0.654462 1.01639 0.998587 0.841047C1.22557 0.725395 1.46382 0.68125 1.71157 0.661008C1.94795 0.641695 2.23633 0.641702 2.57725 0.641711ZM10.2674 9.43216L9.88359 9.04247C9.62469 8.77364 9.45313 8.59607 9.31074 8.47063C9.17308 8.34936 9.09899 8.31037 9.04817 8.29305C8.89927 8.24228 8.73804 8.24042 8.58801 8.28775C8.53681 8.30391 8.46183 8.34118 8.32143 8.45925C8.20901 8.55379 8.07839 8.67913 7.90219 8.85283L9.85038 10.8354C9.85538 10.8331 9.86005 10.8309 9.86444 10.8287C10.0033 10.7579 10.1162 10.645 10.1869 10.5062C10.2112 10.4586 10.237 10.3799 10.2517 10.1996C10.2665 10.0183 10.2673 9.78325 10.2674 9.43216ZM8.39332 10.9091L6.7458 9.23248L5.6463 8.14214C5.38667 7.88468 5.21485 7.71489 5.07269 7.59516C4.93546 7.47957 4.86192 7.44256 4.8113 7.42621C4.66381 7.37856 4.50506 7.37856 4.35756 7.42621C4.30695 7.44256 4.23341 7.47957 4.09617 7.59516C3.9543 7.71465 3.78288 7.88401 3.5241 8.14063L1.17273 10.5089C1.24357 10.6465 1.35589 10.7584 1.49385 10.8287C1.54141 10.8529 1.62012 10.8787 1.8004 10.8934C1.98705 10.9087 2.23054 10.9091 2.59893 10.9091H8.39332Z"
                fill="#3865FD"
                fill-rule="evenodd"/>
            </svg>
          </span>
      </bc-button>
      <input
        ref="fileInput"
        accept="image/png, image/jpeg"
        hidden
        type="file"
        @change="handleFileSelect"/>
    </div>
    <div
      v-if="canPin">
      <bc-checkbox
        :id="`pin-${Math.random()}`"
        :value="pin"
        @input="$emit('update:pin', $event)"
      >
        <i class="icon-pin mr-1"/>
        <p
          class="text-sm">
          {{ $t('generics.pin-the-note') }}
        </p>
      </bc-checkbox>
    </div>
    <div
      v-tooltip="tooltipContent"
      class="ml-auto"
    >
      <bc-button
        ref="timeline-editor-submit"
        :disabled="(!isValid && !images.length) || isLoading"
        class="max-h-9"
        data-button-submit
        @click="sendNote"
      >
        <bc-spinner v-if="isLoading" size="small" color="secondary"></bc-spinner>
        <span v-else>{{ $t('generics.validate') }}</span>
      </bc-button>
    </div>
  </div>
</template>

<style lang="scss" scoped>

</style>
